import { useEffect } from 'react';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, LabelS } from '@sorare/core/src/atoms/typography';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import {
  GqlFilterTitle,
  useBackendRangeSlider,
} from '@sorare/core/src/contexts/graphql-search';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useRangeSlider } from '@sorare/core/src/hooks/useRangeSlider';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import {
  FLOOR_PRICES_FILTERS,
  MARKET_VALUES_FILTERS,
} from '@sorare/core/src/lib/filters';
import {
  PlayerSearchViewAttributes,
  PriceDisplay,
} from '@sorare/core/src/lib/playerSearchView';
import {
  floorPricesAttributes,
  marketValuesAttributes,
} from '@sorare/core/src/lib/scouting-center';
import { powerAlgorithm } from '@sorare/core/src/lib/slider';

import { useFormatFloorPricesFilters } from '#src/hooks/filters/useFormatFloorPricesFilters.ts';
import { useFormatMarketValuesFilters } from '#src/hooks/filters/useFormatMarketValuesFilters.ts';
import {
  BackendRangeSlider,
  RangeSlider,
} from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';
import {
  MarketValueRarityPicker,
  useMarketValueFilterRarity,
} from '#src/searchCommonPlayer/MarketValueRarityPicker/index.tsx';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

const refinePriceAlgorithm = powerAlgorithm({ power: 20 });

const REFINE_PRICE_MAX_EUR = 20000000;

/* Same ceiling as floor prices, expressed in the major units the index uses for market values. */
const REFINE_MARKET_VALUE_MAX_EUR = REFINE_PRICE_MAX_EUR / 100;

const CurrencySymbol = styled(BodyL)`
  align-self: center;
`;
const EthSymbol = styled(LabelS)`
  align-self: flex-end;
`;

const FloorPricesRangeSlider = ({ filter }: { filter: Filter }) => {
  const { statsManager } = useManagePlayerSearchView();
  const currentPriceFilter = floorPricesAttributes(
    statsManager[PlayerSearchViewAttributes.RARITY],
    statsManager[PlayerSearchViewAttributes.SEASONALITY]
  );
  const visible =
    statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY] ===
      PriceDisplay.FLOOR_PRICE && currentPriceFilter === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatFloorPricesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();
  const { refine } = useRangeSlider({
    attribute: filter.attribute,
  });
  useEffect(() => {
    if (!visible) refine([undefined, undefined]);
  }, [refine, visible]);

  return (
    <RangeSlider
      attribute={filter.attribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_EUR)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <CurrencySymbol bold color="var(--c-white)">
            {fiatCurrencySymbol}
          </CurrencySymbol>
        ) : (
          <EthSymbol bold color="var(--c-white)">
            {cryptocurrencySymbol}
          </EthSymbol>
        )
      }
      visible={visible}
      disabled={!visible}
    />
  );
};

const BackendFloorPricesRangeSlider = ({
  filter,
}: {
  filter: Filter & { urlAttribute: string };
}) => {
  const { statsManager } = useManagePlayerSearchView();
  const currentPriceFilter = floorPricesAttributes(
    statsManager[PlayerSearchViewAttributes.RARITY],
    statsManager[PlayerSearchViewAttributes.SEASONALITY]
  );

  const visible =
    statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY] ===
      PriceDisplay.FLOOR_PRICE && currentPriceFilter === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatFloorPricesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();

  const { refine, start } = useBackendRangeSlider({
    attribute: filter.urlAttribute,
  });

  useEffect(() => {
    if (!visible && start.filter(e => e !== undefined).length)
      refine([undefined, undefined]);
  }, [refine, visible, start]);

  return (
    <BackendRangeSlider
      attribute={filter.urlAttribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_EUR)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <CurrencySymbol bold color="var(--c-white)">
            {fiatCurrencySymbol}
          </CurrencySymbol>
        ) : (
          <EthSymbol bold color="var(--c-white)">
            {cryptocurrencySymbol}
          </EthSymbol>
        )
      }
      visible={visible}
      disabled={!visible}
    />
  );
};

const BackendMarketValuesRangeSlider = ({
  filter,
}: {
  filter: Filter & { urlAttribute: string };
}) => {
  const { statsManager } = useManagePlayerSearchView();
  const seasonality = statsManager[PlayerSearchViewAttributes.SEASONALITY];
  const { filterRarity } = useMarketValueFilterRarity();

  const visible =
    statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY] ===
      PriceDisplay.MARKET_VALUE &&
    filterRarity !== undefined &&
    marketValuesAttributes(filterRarity, seasonality) === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatMarketValuesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();

  const { refine, start } = useBackendRangeSlider({
    attribute: filter.urlAttribute,
  });

  useEffect(() => {
    if (!visible && start.filter(e => e !== undefined).length)
      refine([undefined, undefined]);
  }, [refine, visible, start]);

  if (!visible) return null;

  return (
    <Vertical>
      <FilterSection>
        <MarketValueRarityPicker />
      </FilterSection>
      <BackendRangeSlider
        attribute={filter.urlAttribute}
        algorithm={refinePriceAlgorithm}
        fromInput={priceToSlider}
        toInput={sliderToPrice}
        maxForced={REFINE_MARKET_VALUE_MAX_EUR}
        greaterThanMode
        icon={
          currency === Currency.FIAT ? (
            <CurrencySymbol bold color="var(--c-white)">
              {fiatCurrencySymbol}
            </CurrencySymbol>
          ) : (
            <EthSymbol bold color="var(--c-white)">
              {cryptocurrencySymbol}
            </EthSymbol>
          )
        }
        visible
      />
    </Vertical>
  );
};

export const BackendRefineMarketValues = (hideTitle: boolean): FilterWidget[] =>
  Object.values(MARKET_VALUES_FILTERS).map(filter => ({
    key: filter.urlAttribute,
    type: 'range',
    attribute: filter.urlAttribute,
    Component: () => <BackendMarketValuesRangeSlider filter={filter} />,
    title: !hideTitle && (
      <GqlFilterTitle name={filter.urlAttribute} title={filter.title} />
    ),
    accordionOptions: { startsOpen: true },
  }));

const MarketValuesRangeSlider = ({ filter }: { filter: Filter }) => {
  const { statsManager } = useManagePlayerSearchView();
  const seasonality = statsManager[PlayerSearchViewAttributes.SEASONALITY];
  const { filterRarity } = useMarketValueFilterRarity();

  const visible =
    statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY] ===
      PriceDisplay.MARKET_VALUE &&
    marketValuesAttributes(filterRarity, seasonality) === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatMarketValuesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();
  const { refine } = useRangeSlider({ attribute: filter.attribute });

  useEffect(() => {
    if (!visible) refine([undefined, undefined]);
  }, [refine, visible]);

  if (!visible) return null;

  return (
    <Vertical>
      <FilterSection>
        <MarketValueRarityPicker />
      </FilterSection>
      <RangeSlider
        attribute={filter.attribute}
        algorithm={refinePriceAlgorithm}
        fromInput={priceToSlider}
        toInput={sliderToPrice}
        maxForced={REFINE_MARKET_VALUE_MAX_EUR}
        greaterThanMode
        icon={
          currency === Currency.FIAT ? (
            <CurrencySymbol bold color="var(--c-white)">
              {fiatCurrencySymbol}
            </CurrencySymbol>
          ) : (
            <EthSymbol bold color="var(--c-white)">
              {cryptocurrencySymbol}
            </EthSymbol>
          )
        }
        visible
      />
    </Vertical>
  );
};

export const RefineMarketValues = (hideTitle: boolean): FilterWidget[] =>
  Object.values(MARKET_VALUES_FILTERS).map((filter: Filter) =>
    makeRangeFilter(() => <MarketValuesRangeSlider filter={filter} />, filter, {
      startsOpen: true,
      hideTitle,
    })
  );

export const RefineFloorPrices = (hideTitle: boolean): FilterWidget[] =>
  Object.values(FLOOR_PRICES_FILTERS).map((filter: Filter) => {
    return makeRangeFilter(
      () => <FloorPricesRangeSlider filter={filter} />,
      filter,
      {
        startsOpen: true,
        hideTitle,
      }
    );
  });

export const BackendRefineFloorPrices = (hideTitle: boolean): FilterWidget[] =>
  Object.values(FLOOR_PRICES_FILTERS).map(filter => {
    return {
      key: filter.urlAttribute,
      type: 'range',
      attribute: filter.urlAttribute,
      Component: () => <BackendFloorPricesRangeSlider filter={filter} />,
      title: !hideTitle && (
        <GqlFilterTitle name={filter.urlAttribute} title={filter.title} />
      ),
      accordionOptions: { startsOpen: true },
    };
  });
